import { useEffect, useRef, useState } from 'react'
import { useAccountEnv, isApiError } from './client'
import type { CustomerChatAction, CustomerChatMessage } from './types'

type ExtendAction = Extract<CustomerChatAction, { kind: 'extend_session' }>
type EmailReceiptAction = Extract<CustomerChatAction, { kind: 'email_receipt' }>
type RefundAction = Extract<CustomerChatAction, { kind: 'refund_request' }>

/**
 * AveryChat — first-party, authenticated assistant, shared by both host apps
 * (account.parkify.com's portal and pay.parkify.com's checkout account pane).
 *
 * Talks to POST /api/customer/chat with the signed-in customer's session (so
 * Avery already knows who the customer is — no plate/email prompts). Renders
 * the conversation, a typing state, and any action cards the assistant
 * returns (receipt downloads, extend-session, email-receipt, refund-request).
 *
 * Ported verbatim from the portal's original parkify-portal/src/components/
 * AveryChat.tsx — only the API surface (now useAccountEnv().client) and a
 * couple of CSS class names change. The money-relevant confirmExtend guard
 * (fresh idempotency key + double-tap lock) is unchanged byte-for-byte.
 */

interface UiMessage {
  role: 'user' | 'assistant'
  content: string
  actions?: CustomerChatAction[]
}

const GREETING =
  "Hi — I'm Avery. I can pull up your receipts, parking history, monthly spend, and explain charges. What do you need?"

// Quick-start pills shown under the greeting until the customer says something.
// A blank chat box with a blinking cursor is the worst first impression an
// assistant can make — these name the things people actually contact us about
// so the first tap is a real answer instead of a guess at what Avery knows.
//
// Sourced from the live cs capability registry (parkify-cs/app/services/
// capability_registry.py), narrowed to the capabilities Avery can genuinely
// resolve for an AUTHENTICATED parker in this chat — a pill that leads to
// "what's your plate?" is worse than no pill:
//   receipt_request · refund_request · extend_or_cancel_session ·
//   validation ("charged after being validated") · enforcement · spend
// Keep this list short; six is already the point where pills stop being
// scannable and start being a menu.
const DEFAULT_QUICK_PROMPTS = [
  'Send me a receipt',
  'Why was I charged?',
  'Extend my session',
  'Request a refund',
  'I got a ticket after paying',
  'How much have I spent this month?',
] as const

// The customer's local timezone, so "this month" and dates resolve to their
// calendar rather than UTC.
const TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York' }
  catch { return 'America/New_York' }
})()

export function AveryChat({
  seed = '',
  onClose,
  quickPrompts = DEFAULT_QUICK_PROMPTS,
}: {
  seed?: string
  onClose: () => void
  // Host override for the quick-start pills (pass [] to hide them entirely).
  quickPrompts?: readonly string[]
}) {
  const { client } = useAccountEnv()
  const [messages, setMessages] = useState<UiMessage[]>([{ role: 'assistant', content: GREETING }])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  // Per-extend-card state, keyed by `${txnId}:${minutes}` so re-renders keep
  // the button disabled/confirmed correctly.
  const [extendState, setExtendState] = useState<Record<string, 'busy' | 'done'>>({})
  // Email-receipt confirm state, keyed by sessionId.
  const [emailState, setEmailState] = useState<Record<string, 'busy' | 'done'>>({})
  // Refund-request confirm state, keyed by sessionId.
  const [refundState, setRefundState] = useState<Record<string, 'busy' | 'done'>>({})
  const scrollRef = useRef<HTMLDivElement>(null)
  const seedSentRef = useRef(false)

  // Auto-scroll to the newest message / typing indicator.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, busy])

  const send = async (text: string) => {
    const t = text.trim()
    if (!t || busy) return
    // API history excludes the canned greeting (the model's turns must start
    // with a user message), then appends the new question.
    const apiHistory: CustomerChatMessage[] = messages
      .slice(1)
      .map(m => ({ role: m.role, content: m.content }))
    apiHistory.push({ role: 'user', content: t })

    setMessages(prev => [...prev, { role: 'user', content: t }])
    setInput('')
    setBusy(true)
    try {
      const res = await client.sendCustomerChat(apiHistory, TZ)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.reply,
        actions: res.actions,
      }])
    } catch (e) {
      const msg = isApiError(e) && e.status === 401
        ? 'Your session expired — please sign in again.'
        : "Sorry — I couldn't reach the assistant just now. Please try again in a moment."
      setMessages(prev => [...prev, { role: 'assistant', content: msg }])
    } finally {
      setBusy(false)
    }
  }

  // Auto-send the question typed in the header box.
  useEffect(() => {
    if (seedSentRef.current) return
    if (seed && seed.trim()) {
      seedSentRef.current = true
      void send(seed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const downloadReceipt = async (sessionId: string) => {
    if (downloading) return
    setDownloading(sessionId)
    try {
      const blob = await client.fetchReceipt(sessionId)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I couldn't open that receipt just now — please try again.",
      }])
    } finally {
      setDownloading(null)
    }
  }

  // Confirm an extend proposal → calls the existing authenticated extend
  // endpoint (the only place a charge happens). Idempotency key guards against
  // a double-tap producing two charges.
  const confirmExtend = async (a: ExtendAction) => {
    const key = `${a.txnId}:${a.minutes}`
    if (extendState[key]) return
    setExtendState(s => ({ ...s, [key]: 'busy' }))
    try {
      const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${a.txnId}-${a.minutes}-${Date.now()}`
      const res = await client.extendSession({ txnId: a.txnId, additionalMinutes: a.minutes, idempotencyKey })
      const newTime = new Date(res.newExpiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      const charged = (res.chargedCents / 100).toFixed(2)
      setExtendState(s => ({ ...s, [key]: 'done' }))
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Done — added ${res.addedMinutes} min. Your session now runs until ${newTime}.` +
          (res.chargedCents > 0 ? ` Charged $${charged} to your saved card.` : ' No extra charge.'),
      }])
    } catch (e) {
      setExtendState(s => { const n = { ...s }; delete n[key]; return n })
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: isApiError(e) ? e.message : "I couldn't add the time just now — please try again.",
      }])
    }
  }

  // Confirm an email-receipt proposal → emails the PDF to the customer.
  const confirmEmail = async (a: EmailReceiptAction) => {
    const key = a.sessionId
    if (emailState[key]) return
    setEmailState(s => ({ ...s, [key]: 'busy' }))
    try {
      await client.emailReceipt(a.sessionId)
      setEmailState(s => ({ ...s, [key]: 'done' }))
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sent — your receipt for ${a.zoneName} on ${a.date} is on its way to ${a.emailMasked}. Check your inbox (and spam, just in case).`,
      }])
    } catch (e) {
      setEmailState(s => { const n = { ...s }; delete n[key]; return n })
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: isApiError(e) ? e.message : "I couldn't email the receipt just now — please try again.",
      }])
    }
  }

  // Confirm a refund proposal → FILES a request for human review. No money moves.
  const confirmRefund = async (a: RefundAction) => {
    const key = a.sessionId
    if (refundState[key]) return
    setRefundState(s => ({ ...s, [key]: 'busy' }))
    try {
      const res = await client.fileRefundRequest(a.sessionId, a.reason, a.requestedAmountUsd)
      setRefundState(s => ({ ...s, [key]: 'done' }))
      // Confirm the amount the customer actually asked for (partial when set).
      const askedUsd = a.requestedAmountUsd ?? a.amountUsd
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Your refund request for ${a.zoneName} on ${a.date} ($${askedUsd.toFixed(2)}) is in. Our team reviews each request — you'll hear back ${res.humanEta}.`,
      }])
    } catch (e) {
      setRefundState(s => { const n = { ...s }; delete n[key]; return n })
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: isApiError(e) ? e.message : "I couldn't file the refund request just now — please try again.",
      }])
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void send(input)
  }

  // Auto-grow the composer with its content (up to a cap, then it scrolls
  // internally) so a long message wraps into view instead of scrolling
  // sideways in a single-line field. Reset to `auto` first so shrinking a
  // message (e.g. after Backspace) actually shrinks the box, not just grows.
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [input])

  // Pills are a cold-start affordance only: they disappear the moment the
  // conversation has a real turn (or is about to, via `seed`).
  const showQuickPrompts =
    quickPrompts.length > 0 && messages.length === 1 && !busy && !seed.trim()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--color-white)' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 14px',
        background: 'linear-gradient(135deg, #3d1a5c 0%, #7F3F98 100%)',
        color: 'var(--color-white)',
        flex: '0 0 auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span aria-hidden style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 24, height: 24, borderRadius: '50%',
            background: 'var(--color-white)', color: 'var(--color-brand)',
            fontWeight: 800, fontSize: 12,
          }}>A</span>
          <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.2 }}>Avery</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close Avery"
          style={{
            color: 'var(--color-white)', fontSize: 22, lineHeight: 1,
            width: 30, height: 30, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >×</button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start', gap: 6 }}>
            <div style={{
              maxWidth: '85%',
              padding: '9px 12px',
              borderRadius: 14,
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              background: m.role === 'user' ? 'var(--color-brand)' : 'var(--color-gray-100)',
              color: m.role === 'user' ? 'var(--color-white)' : 'var(--color-black)',
              borderTopRightRadius: m.role === 'user' ? 4 : 14,
              borderTopLeftRadius: m.role === 'user' ? 14 : 4,
            }}>
              {m.content}
            </div>
            {m.actions?.map((a, j) => {
              if (a.kind === 'receipt_download') {
                return (
                  <button
                    key={j}
                    type="button"
                    onClick={() => downloadReceipt(a.sessionId)}
                    disabled={downloading === a.sessionId}
                    className="pka-btn-text"
                    style={{
                      fontSize: 13, fontWeight: 700, padding: '8px 12px',
                      border: '1px solid var(--color-brand-light)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-brand-light)', textAlign: 'left',
                    }}
                  >
                    {downloading === a.sessionId ? 'Opening…' : `↓ ${a.label}`}
                  </button>
                )
              }
              if (a.kind === 'start_session_link') {
                return (
                  <a
                    key={j}
                    href={a.checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pka-btn-text"
                    style={{
                      fontSize: 13, fontWeight: 700, padding: '8px 12px',
                      border: '1px solid var(--color-brand-light)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-brand-light)', textAlign: 'left',
                      textDecoration: 'none', display: 'inline-block',
                    }}
                  >
                    {`↗ ${a.label}`}
                  </a>
                )
              }
              if (a.kind === 'extend_session') {
                // confirm card — the only place a charge is triggered
                const key = `${a.txnId}:${a.minutes}`
                const st = extendState[key]
                return (
                  <div key={j} style={{
                    width: '85%',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-md)',
                    padding: 12,
                    background: 'var(--color-white)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-black)' }}>
                      Add {a.minutes} min · ${a.quoteUsd.toFixed(2)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>
                      {a.zoneName} · charges your saved card · runs until{' '}
                      {new Date(a.newExpiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </div>
                    <button
                      type="button"
                      onClick={() => confirmExtend(a)}
                      disabled={!!st}
                      className="pka-btn-primary"
                      style={{ alignSelf: 'flex-start', padding: '8px 16px', fontSize: 13 }}
                    >
                      {st === 'busy' ? 'Adding…' : st === 'done' ? '✓ Time added' : 'Confirm & add time'}
                    </button>
                  </div>
                )
              }
              if (a.kind === 'email_receipt') {
                // confirm card (emails the PDF on confirm)
                const est = emailState[a.sessionId]
                return (
                  <div key={j} style={{
                    width: '85%',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-md)',
                    padding: 12,
                    background: 'var(--color-white)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-black)' }}>
                      Email receipt
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>
                      {a.zoneName} · {a.date} · to {a.emailMasked}
                    </div>
                    <button
                      type="button"
                      onClick={() => confirmEmail(a)}
                      disabled={!!est}
                      className="pka-btn-primary"
                      style={{ alignSelf: 'flex-start', padding: '8px 16px', fontSize: 13 }}
                    >
                      {est === 'busy' ? 'Sending…' : est === 'done' ? '✓ Sent' : 'Confirm & email receipt'}
                    </button>
                  </div>
                )
              }
              // refund_request — confirm card (FILES a request for review; no money moves)
              const rst = refundState[a.sessionId]
              return (
                <div key={j} style={{
                  width: '85%',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: 'var(--radius-md)',
                  padding: 12,
                  background: 'var(--color-white)',
                  display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-black)' }}>
                    Request a refund · ${(a.requestedAmountUsd ?? a.amountUsd).toFixed(2)}
                    {a.requestedAmountUsd != null && a.requestedAmountUsd < a.amountUsd
                      ? <span style={{ fontWeight: 600, color: 'var(--color-gray-600)' }}> of ${a.amountUsd.toFixed(2)}</span>
                      : null}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>
                    {a.zoneName} · {a.date} · our team reviews every request
                  </div>
                  <button
                    type="button"
                    onClick={() => confirmRefund(a)}
                    disabled={!!rst}
                    className="pka-btn-primary"
                    style={{ alignSelf: 'flex-start', padding: '8px 16px', fontSize: 13 }}
                  >
                    {rst === 'busy' ? 'Submitting…' : rst === 'done' ? '✓ Request submitted' : 'Submit refund request'}
                  </button>
                </div>
              )
            })}
          </div>
        ))}
        {/* Quick-start pills — only while the greeting is the whole conversation.
            Suppressed when a `seed` was supplied (AveryAsk's header box already
            asked the question, so the answer is one tick away and the pills
            would flash on first paint). Sending one is exactly a typed message:
            it goes through the same send(), so history/critic/actions behave
            identically to the customer typing it. */}
        {showQuickPrompts && (
          <div
            role="group"
            aria-label="Common questions"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 2 }}
          >
            {quickPrompts.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => void send(p)}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  padding: '7px 13px',
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--color-brand-border)',
                  background: 'var(--color-brand-light)',
                  color: 'var(--color-brand)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 120ms ease, border-color 120ms ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--color-white)'
                  e.currentTarget.style.borderColor = 'var(--color-brand)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'var(--color-brand-light)'
                  e.currentTarget.style.borderColor = 'var(--color-brand-border)'
                }}
              >
                {p}
              </button>
            ))}
          </div>
        )}
        {busy && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', gap: 4, padding: '10px 12px' }}>
            {[0, 1, 2].map(d => (
              <span key={d} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--color-gray-400)',
                animation: 'pka-spin 0.9s linear infinite',
                opacity: 0.6,
              }} />
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={onSubmit} style={{ flex: '0 0 auto', display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--color-gray-200)' }}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            // Enter sends, like the old single-line input; Shift+Enter still
            // inserts a newline for a genuinely multi-line message.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send(input)
            }
          }}
          placeholder="Ask Avery about your parking…"
          aria-label="Message Avery"
          enterKeyHint="send"
          rows={1}
          autoFocus
          style={{
            flex: 1, minWidth: 0,
            border: '1.5px solid var(--color-gray-300)',
            borderRadius: 16,
            padding: '9px 14px',
            fontSize: 14,
            fontFamily: 'inherit',
            lineHeight: 1.4,
            outline: 'none',
            resize: 'none',
            maxHeight: 120,
            overflowY: 'auto',
          }}
        />
        <button
          type="submit"
          className="pka-btn-primary"
          disabled={busy || input.trim().length === 0}
          style={{ padding: '9px 18px', alignSelf: 'flex-end' }}
        >
          Send
        </button>
      </form>
    </div>
  )
}
